import { useEffect } from 'react';
import useTripStore from '../store/useTripStore';
import { useDay } from './useTripStoreSelectors';
import { useTripDesignerConfig } from '../config/TripDesignerConfigProvider';
import {
  priceExperience,
  ExperiencePriceBase,
  ExperienceExtraOccurrence,
  ExperiencePrice,
} from '../logic/priceExperience';

function isSamePrice(
  a: ExperiencePrice | null | undefined,
  b: ExperiencePrice | null | undefined,
): boolean {
  if (!a || !b) return a === b;
  return a.price === b.price && a.currency === b.currency;
}

// US-059 — the experience price of a day, computed outside the component.
// `basePrice`/`extras` are gathered by the caller, which already fetches the
// program's price and knows which occurrences belong to this instance — the
// two apps disagree on both points (TO refetches and aggregates per day
// instance, DMC only on the instance's first day), not worth unifying here.
// A caller that has nothing to price for this day (DMC on days 2..n of an
// instance) simply passes `null`/`undefined` for `basePrice`.
//
// `day.tripPrice` is the one field every reader (useTripTotal,
// useExperienceFrames) checks first, so it is always written here.
// `day.experiencePricing` also used to carry the same total for TO (its own
// ComposeMap.js reads it directly) while DMC repurposes that same field to
// cache the raw, extras-less API response for its own refetch-skip logic —
// `writeExperiencePricing` lets DMC opt out and keep managing that field
// itself.
export function useTripExperiencePricing(
  dayIndex: number,
  basePrice: ExperiencePriceBase | null | undefined,
  extras: ExperienceExtraOccurrence[] | null | undefined,
  writeExperiencePricing: boolean = true,
): ExperiencePrice | null {
  const day = useDay(dayIndex);
  const experienceId = day?.experienceId;
  const experience = useTripStore((state) =>
    experienceId ? state.experiences[experienceId] : undefined,
  );
  const updateDay = useTripStore((state) => state.updateDay);
  const { getExperiencePriceOverride } = useTripDesignerConfig();

  const priced: ExperiencePrice | null =
    basePrice?.price === undefined
      ? null
      : ((getExperiencePriceOverride?.(experience, extras ?? []) ??
          priceExperience(basePrice, extras)) as ExperiencePrice | null);

  const isWritable =
    !!priced &&
    !Number.isNaN(priced.price) &&
    !!priced.currency &&
    priced.currency !== 'mismatch';

  useEffect(() => {
    if (!isWritable) return;

    const updates: Record<string, ExperiencePrice> = {};
    if (!isSamePrice(priced, day?.tripPrice)) {
      updates.tripPrice = priced;
    }
    if (
      writeExperiencePricing &&
      !isSamePrice(priced, day?.experiencePricing)
    ) {
      updates.experiencePricing = priced;
    }
    if (Object.keys(updates).length > 0) {
      updateDay(dayIndex, updates);
    }
    // `priced`/`day` are read fresh from the closure on every call; only the
    // fields that decide whether a write is needed belong in the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dayIndex,
    isWritable,
    priced?.price,
    priced?.currency,
    day?.tripPrice?.price,
    day?.tripPrice?.currency,
    day?.experiencePricing?.price,
    day?.experiencePricing?.currency,
    writeExperiencePricing,
    updateDay,
  ]);

  return priced;
}
